import React, { useEffect, useRef, useState } from "react";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import DropdownComponent from "../../components/form-control/drop-down";
import apiFunctions from "../../services/api-service";
import Chart from "chart.js/auto";
import LocationMap, { LocationPoint } from "../../components/common/LocationMap";

type Summary = {
    activeUsers: number;
    newUsers: number;
    sessions: number;
    engagedSessions: number;
    engagementRate: number;
    averageSessionDurationSec: number;
    pageViews: number;
    bounceRate: number;
    eventCount: number;
};

type TrendPoint = { date: string; activeUsers: number; sessions: number };
type TopPage = {
    path: string; title: string; views: number; avgDurationSec: number;
    activeUsers: number; engagedSessions: number; engagementRate: number; eventCount: number;
};
type TrafficSource = { channel: string; sessions: number; activeUsers: number };
type DeviceRow = { device: string; activeUsers: number };
type CityRow = { city: string; activeUsers: number };
type CityReportRow = CityRow & {
    newUsers: number; engagedSessions: number; engagementRate: number; avgDurationSec: number; eventCount: number;
};
type SourceRow = { source: string; activeUsers: number };
type SessionSourceRow = { source: string; sessions: number };
type NewVsReturningPoint = { date: string; new: number; returning: number };
type MinuteRow = { minutesAgo: number; activeUsers: number };

const EMPTY_SUMMARY: Summary = {
    activeUsers: 0, newUsers: 0, sessions: 0, engagedSessions: 0,
    engagementRate: 0, averageSessionDurationSec: 0, pageViews: 0, bounceRate: 0, eventCount: 0,
};

// GA4 dates come back as YYYYMMDD (no separators) — reformat for the chart's x-axis.
const formatGaDate = (d: string) => {
    if (!d || d.length !== 8) return d;
    return `${d.slice(6, 8)}/${d.slice(4, 6)}`;
};

const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return `${m}m ${s}s`;
};

const formatPercent = (v: number) => `${(v * 100).toFixed(1)}%`;

// Abbreviated everywhere a count is shown (matches GA4's own "37k" style) —
// the exact value is still available via the title tooltip on hover. Tiers
// are a table, not hardcoded if/else branches, so this keeps scaling past B
// on its own (T, then Q for quadrillion) instead of getting stuck at the
// last tier once a number is that large — numbers below 1000 are left as
// plain digits, unabbreviated.
const COMPACT_TIERS: { value: number; suffix: string }[] = [
    { value: 1_000_000_000_000_000, suffix: "Q" },
    { value: 1_000_000_000_000, suffix: "T" },
    { value: 1_000_000_000, suffix: "B" },
    { value: 1_000_000, suffix: "M" },
    { value: 1_000, suffix: "K" },
];
const formatCompact = (n: number) => {
    const tier = COMPACT_TIERS.find((t) => n >= t.value);
    return tier ? `${(n / tier.value).toFixed(1)}${tier.suffix}` : String(n);
};

const CHANNEL_COLORS: Record<string, string> = {
    "Organic Search": "#2a8f9e",
    "Direct": "#264164",
    "Referral": "#f59e0b",
    "Organic Social": "#8b5cf6",
    "Paid Search": "#ef4444",
    "Email": "#10b981",
    "Unassigned": "#9ca3af",
};

// Compact K/M/B value with the exact number on hover — used for every
// numeric table cell so report tables stay consistent with the stat cards.
const CompactCell: React.FC<{ n: number }> = ({ n }) => (
    <span title={n.toLocaleString()} style={{ cursor: "default" }}>{formatCompact(n)}</span>
);

const WebsiteAnalyticsPage: React.FC = () => {
    const [source, setSource] = useState<"web" | "app">("web");
    const [range, setRange] = useState("30d");
    const [rangeOptions] = useState([
        { name: "Last 7 days", id: "7d" },
        { name: "Last 30 days", id: "30d" },
        { name: "Last 90 days", id: "90d" },
    ]);

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");
    const [summary, setSummary] = useState<Summary>(EMPTY_SUMMARY);
    const [dailyTrend, setDailyTrend] = useState<TrendPoint[]>([]);
    const [topPages, setTopPages] = useState<TopPage[]>([]);
    const [trafficSources, setTrafficSources] = useState<TrafficSource[]>([]);
    const [deviceBreakdown, setDeviceBreakdown] = useState<DeviceRow[]>([]);
    const [cityBreakdown, setCityBreakdown] = useState<CityReportRow[]>([]);
    const [realtimeActiveUsers, setRealtimeActiveUsers] = useState(0);
    const [realtimeByCity, setRealtimeByCity] = useState<CityRow[]>([]);
    const [realtimeByMinute, setRealtimeByMinute] = useState<MinuteRow[]>([]);
    const [firstUserSource, setFirstUserSource] = useState<SourceRow[]>([]);
    const [sessionSource, setSessionSource] = useState<SessionSourceRow[]>([]);
    const [newVsReturningTrend, setNewVsReturningTrend] = useState<NewVsReturningPoint[]>([]);
    const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

    // GA4 only gives city names, not coordinates — the registered-users
    // locations endpoint already has a real, averaged lat/lng per city name
    // (from users' own saved current-location data), so we reuse that as a
    // lookup table and join it against cityBreakdown by city name instead of
    // adding a separate geocoding step.
    const [cityCoords, setCityCoords] = useState<Record<string, { lat: number; lng: number }>>({});
    useEffect(() => {
        (async () => {
            try {
                const res = await apiFunctions.get("/user/dashboard/locations");
                if (res?.data?.success) {
                    const lookup: Record<string, { lat: number; lng: number }> = {};
                    (res.data.locations || []).forEach((loc: { city: string; lat: number; lng: number }) => {
                        lookup[loc.city.trim().toLowerCase()] = { lat: loc.lat, lng: loc.lng };
                    });
                    setCityCoords(lookup);
                }
            } catch (err) {
                console.error("Failed to load city coordinates:", err);
            }
        })();
    }, []);

    // Tied to the realtime "Active Users (Last 30 Minutes)" widget above,
    // not the date-range cityBreakdown — same live data as the "By City"
    // list next to it, just joined with real coordinates so it can be
    // plotted.
    const activeUserLocations: LocationPoint[] = realtimeByCity.reduce<LocationPoint[]>((acc, row) => {
        const coords = cityCoords[row.city.trim().toLowerCase()];
        if (coords && row.activeUsers > 0) {
            acc.push({ city: row.city, count: row.activeUsers, lat: coords.lat, lng: coords.lng });
        }
        return acc;
    }, []);

    const trendCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const trendChartRef = useRef<Chart | null>(null);
    const nvrCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const nvrChartRef = useRef<Chart | null>(null);
    const minuteCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const minuteChartRef = useRef<Chart | null>(null);

    const fetchData = async (isRefresh = false) => {
        if (isRefresh) setRefreshing(true);
        else setLoading(true);
        setError("");
        try {
            const res = await apiFunctions.get(`/analytics/overview?range=${range}&source=${source}`);
            if (res?.data?.success) {
                const d = res.data.data;
                setSummary(d.summary || EMPTY_SUMMARY);
                setDailyTrend(d.dailyTrend || []);
                setTopPages(d.topPages || []);
                setTrafficSources(d.trafficSources || []);
                setDeviceBreakdown(d.deviceBreakdown || []);
                setCityBreakdown(d.cityBreakdown || []);
                setFirstUserSource(d.firstUserSource || []);
                setSessionSource(d.sessionSource || []);
                setNewVsReturningTrend(d.newVsReturningTrend || []);
                setRealtimeActiveUsers(d.realtimeActiveUsers || 0);
                setRealtimeByCity(d.realtimeByCity || []);
                setRealtimeByMinute(d.realtimeByMinute || []);
                setLastUpdated(new Date());
            } else {
                setError(res?.data?.message || "Failed to load analytics");
            }
        } catch (err) {
            console.error("Failed to load website analytics:", err);
            setError("Failed to load analytics — check that the GA4 credentials are configured on the server.");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [range, source]);

    // Users/Sessions trend line chart
    useEffect(() => {
        if (!trendCanvasRef.current) return;

        trendChartRef.current?.destroy();
        trendChartRef.current = new Chart(trendCanvasRef.current, {
            type: "line",
            data: {
                labels: dailyTrend.map((p) => formatGaDate(p.date)),
                datasets: [
                    {
                        label: "Active Users",
                        data: dailyTrend.map((p) => p.activeUsers),
                        borderColor: "#2a8f9e",
                        backgroundColor: "rgba(42, 143, 158, 0.12)",
                        fill: true,
                        tension: 0.3,
                        pointRadius: 2,
                    },
                    {
                        label: "Sessions",
                        data: dailyTrend.map((p) => p.sessions),
                        borderColor: "#264164",
                        backgroundColor: "rgba(38, 65, 100, 0.08)",
                        fill: true,
                        tension: 0.3,
                        pointRadius: 2,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                // Hovering anywhere on the x-axis (not just directly on a
                // point) shows both series for that date together.
                interaction: { mode: "index" as const, intersect: false },
                plugins: {
                    legend: { position: "top", labels: { color: "#374151", font: { weight: "bold" as const } } },
                    tooltip: {
                        mode: "index" as const,
                        intersect: false,
                        backgroundColor: "#1f2937",
                        padding: 10,
                        titleFont: { weight: "bold" as const },
                        bodySpacing: 4,
                        // Compact form here too (matches every other number on
                        // the page), full value still on the y-axis's own
                        // hover-independent title via CompactCell elsewhere.
                        callbacks: {
                            label: (ctx) => {
                                const y = ctx.parsed.y ?? 0;
                                return `${ctx.dataset.label}: ${formatCompact(y)} (${y.toLocaleString()})`;
                            },
                        },
                    },
                },
                scales: {
                    y: { beginAtZero: true, ticks: { precision: 0, color: "#374151", callback: (v) => formatCompact(Number(v)) } },
                    x: { grid: { display: false }, ticks: { color: "#374151" } },
                },
            },
        });

        return () => {
            trendChartRef.current?.destroy();
            trendChartRef.current = null;
        };
    }, [dailyTrend]);

    // New vs Returning users trend chart
    useEffect(() => {
        if (!nvrCanvasRef.current) return;

        nvrChartRef.current?.destroy();
        nvrChartRef.current = new Chart(nvrCanvasRef.current, {
            type: "line",
            data: {
                labels: newVsReturningTrend.map((p) => formatGaDate(p.date)),
                datasets: [
                    {
                        label: "new",
                        data: newVsReturningTrend.map((p) => p.new),
                        borderColor: "#2563eb",
                        backgroundColor: "rgba(37, 99, 235, 0.1)",
                        fill: true,
                        tension: 0.3,
                        pointRadius: 2,
                    },
                    {
                        label: "returning",
                        data: newVsReturningTrend.map((p) => p.returning),
                        borderColor: "#22c55e",
                        backgroundColor: "rgba(34, 197, 94, 0.1)",
                        fill: true,
                        tension: 0.3,
                        pointRadius: 2,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: "index" as const, intersect: false },
                plugins: {
                    legend: { position: "top", labels: { color: "#374151", font: { weight: "bold" as const } } },
                    tooltip: {
                        mode: "index" as const,
                        intersect: false,
                        backgroundColor: "#1f2937",
                        padding: 10,
                        callbacks: {
                            label: (ctx) => {
                                const y = ctx.parsed.y ?? 0;
                                return `${ctx.dataset.label}: ${formatCompact(y)} (${y.toLocaleString()})`;
                            },
                        },
                    },
                },
                scales: {
                    y: { beginAtZero: true, ticks: { precision: 0, color: "#374151", callback: (v) => formatCompact(Number(v)) } },
                    x: { grid: { display: false }, ticks: { color: "#374151" } },
                },
            },
        });

        return () => {
            nvrChartRef.current?.destroy();
            nvrChartRef.current = null;
        };
    }, [newVsReturningTrend]);

    // Realtime "Active users per minute" bar chart — matches GA4's own
    // Realtime widget. minutesAgo=0 is "now"; rows already arrive
    // oldest-first from the backend.
    useEffect(() => {
        if (!minuteCanvasRef.current) return;

        minuteChartRef.current?.destroy();
        minuteChartRef.current = new Chart(minuteCanvasRef.current, {
            type: "bar",
            data: {
                labels: realtimeByMinute.map((m) => (m.minutesAgo === 0 ? "now" : `-${m.minutesAgo}m`)),
                datasets: [{
                    label: "Active Users",
                    data: realtimeByMinute.map((m) => m.activeUsers),
                    backgroundColor: "#2563eb",
                    borderRadius: 2,
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: false },
                    tooltip: { backgroundColor: "#1f2937", padding: 8 },
                },
                scales: {
                    y: { beginAtZero: true, ticks: { precision: 0, color: "#374151" }, display: false },
                    x: {
                        grid: { display: false },
                        ticks: { color: "#6b7280", font: { size: 9 }, autoSkip: true, maxRotation: 0 },
                    },
                },
            },
        });

        return () => {
            minuteChartRef.current?.destroy();
            minuteChartRef.current = null;
        };
    }, [realtimeByMinute]);

    const maxChannelSessions = Math.max(...trafficSources.map((s) => s.sessions), 1);
    const maxDeviceUsers = Math.max(...deviceBreakdown.map((d) => d.activeUsers), 1);

    // exact is the full-precision number shown on hover (title attribute) —
    // value is the compact K/M/B form shown on the card itself, matching
    // GA4's own dashboard style. Percent/duration cards have no compact form,
    // so exact just repeats value there (hover shows the same text).
    const statCards: { title: string; value: string; exact: string }[] = [
        { title: "Active Users", value: formatCompact(summary.activeUsers), exact: summary.activeUsers.toLocaleString() },
        { title: "New Users", value: formatCompact(summary.newUsers), exact: summary.newUsers.toLocaleString() },
        { title: "Sessions", value: formatCompact(summary.sessions), exact: summary.sessions.toLocaleString() },
        { title: "Engaged Sessions", value: formatCompact(summary.engagedSessions), exact: summary.engagedSessions.toLocaleString() },
        { title: "Engagement Rate", value: formatPercent(summary.engagementRate), exact: formatPercent(summary.engagementRate) },
        { title: "Avg Engagement Time", value: formatDuration(summary.averageSessionDurationSec), exact: formatDuration(summary.averageSessionDurationSec) },
        { title: "Page Views", value: formatCompact(summary.pageViews), exact: summary.pageViews.toLocaleString() },
        { title: "Bounce Rate", value: formatPercent(summary.bounceRate), exact: formatPercent(summary.bounceRate) },
        { title: "Event Count", value: formatCompact(summary.eventCount), exact: summary.eventCount.toLocaleString() },
    ];

    if (loading) {
        return (
            <div className="dashboard-margin">
                <div className="grid grid-cols-12 gap-4 mt-4 items-center">
                    <div className="col-span-12 md:col-span-6">
                        <h2 className="card-title m-0">Website Analytics</h2>
                    </div>
                    <div className="col-span-12 md:col-span-6 flex justify-start md:justify-end">
                        <div className="w-full md:w-64">
                            <Skeleton height="2.5rem" className="rounded" />
                        </div>
                    </div>
                </div>
                <div className="grid grid-cols-12 gap-4 mt-4">
                    {Array.from({ length: 8 }).map((_, i) => (
                        <div className="col-span-12 sm:col-span-6 lg:col-span-3" key={i}>
                            <div className="card h-full">
                                <Card>
                                    <Skeleton height="1rem" width="60%" className="rounded mb-2" />
                                    <Skeleton height="1.75rem" width="40%" className="rounded" />
                                </Card>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="grid grid-cols-12 gap-4 mt-4">
                    <div className="col-span-12">
                        <div className="card h-full">
                            <Card title="Users & Sessions">
                                <Skeleton height="300px" className="rounded" />
                            </Card>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-margin">
            {/* Website/App toggle — its own full-width row above the title,
                each half taking exactly 50% instead of sizing to its text. */}
            <div className="flex rounded-md overflow-hidden mt-4" style={{ border: "1px solid #264164" }}>
                <button
                    type="button"
                    onClick={() => setSource("web")}
                    style={{
                        flex: "1 1 50%", padding: "10px 16px", fontSize: "13px", fontWeight: 600, border: "none", cursor: "pointer",
                        background: source === "web" ? "#264164" : "#ffffff",
                        color: source === "web" ? "#ffffff" : "#264164",
                    }}
                >
                    Website
                </button>
                <button
                    type="button"
                    onClick={() => setSource("app")}
                    style={{
                        flex: "1 1 50%", padding: "10px 16px", fontSize: "13px", fontWeight: 600, border: "none", cursor: "pointer",
                        background: source === "app" ? "#264164" : "#ffffff",
                        color: source === "app" ? "#ffffff" : "#264164",
                    }}
                >
                    App
                </button>
            </div>

            <div className="grid grid-cols-12 gap-4 mt-4 items-center">
                <div className="col-span-12 md:col-span-6">
                    <h2 className="card-title m-0">{source === "app" ? "App Analytics" : "Website Analytics"}</h2>
                    <p className="text-xs m-0 mt-1" style={{ color: "#000000" }}>Live data from Google Analytics (GA4)</p>
                </div>
                <div className="col-span-12 md:col-span-6 flex justify-start md:justify-end items-center gap-3 flex-wrap">
                    <div className="w-full md:w-56">
                        <DropdownComponent
                            id="range"
                            placeholder="Select Range"
                            modelValue={range}
                            options={rangeOptions}
                            onUpdateModelValue={(value: string) => setRange(value)}
                        />
                    </div>
                    <button
                        type="button"
                        onClick={() => fetchData(true)}
                        disabled={refreshing}
                        style={{
                            padding: "8px 16px", fontSize: "13px", fontWeight: 600, borderRadius: "6px",
                            border: "1px solid #264164", background: "#ffffff", color: "#264164",
                            cursor: refreshing ? "default" : "pointer", opacity: refreshing ? 0.6 : 1,
                        }}
                    >
                        {refreshing ? "Refreshing..." : "↻ Refresh"}
                    </button>
                </div>
            </div>

            {lastUpdated && (
                <p className="text-xs m-0 mt-1" style={{ color: "#6b7280", textAlign: "right" }}>
                    Last updated: {lastUpdated.toLocaleTimeString()}
                </p>
            )}

            {error && (
                <div className="mt-4 p-3 rounded-md" style={{ background: "#fef2f2", color: "#b91c1c", fontSize: "13px" }}>
                    {error}
                </div>
            )}

            {/* Realtime — separate from the range-based stats above; always
                scoped to "right now" (last ~30 minutes) via GA4's Realtime API. */}
            <div className="grid grid-cols-12 gap-4 mt-4">
                <div className="col-span-12">
                    <div className="card h-full" style={{ borderLeft: "4px solid #22c55e" }}>
                        <Card>
                            <div className="flex items-start justify-between flex-wrap gap-6">
                                <div style={{ minWidth: "220px" }}>
                                    <p className="text-xs m-0" style={{ color: "#22c55e", fontWeight: 700, letterSpacing: "0.5px" }}>● LIVE</p>
                                    <p className="text-xs m-0 mt-1" style={{ color: "#6b7280" }}>Active Users (Last 30 Minutes)</p>
                                    <span title={realtimeActiveUsers.toLocaleString()} style={{ fontSize: "32px", fontWeight: 700, color: "#264164", cursor: "default" }}>{formatCompact(realtimeActiveUsers)}</span>

                                    <p className="text-xs m-0 mt-4 mb-2" style={{ color: "#6b7280", fontWeight: 700 }}>Active Users Per Minute</p>
                                    <div style={{ height: "85px", width: "220px" }}>
                                        <canvas ref={minuteCanvasRef} />
                                    </div>
                                </div>
                                {realtimeByCity.length > 0 && (
                                    <div style={{ minWidth: "220px" }}>
                                        <p className="text-xs m-0 mb-2" style={{ color: "#6b7280", fontWeight: 700 }}>By City</p>
                                        <div className="flex flex-col">
                                            {realtimeByCity.map((c) => (
                                                <div
                                                    key={c.city}
                                                    className="flex justify-between text-xs"
                                                    style={{ color: "#374151", padding: "6px 0", borderBottom: "1px solid #e5e7eb" }}
                                                >
                                                    <span>{c.city}</span>
                                                    <span title={c.activeUsers.toLocaleString()} style={{ fontWeight: 600, cursor: "default" }}>{formatCompact(c.activeUsers)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Card>
                    </div>
                </div>
            </div>

            {/* Active users by location — tied to the realtime "Active Users
                (Last 30 Minutes)" widget directly above (same data as its
                "By City" list), joined against real per-city coordinates
                from registered users' own saved locations (see cityCoords
                above) so only cities with a known coordinate can be plotted. */}
            <div className="grid grid-cols-12 gap-4 mt-4">
                <div className="col-span-12">
                    <LocationMap
                        title="Active Users by Location"
                        loading={loading}
                        locations={activeUserLocations}
                        subtitle={`${activeUserLocations.reduce((s, l) => s + l.count, 0).toLocaleString()} active users (last 30 minutes) across ${activeUserLocations.length} mapped cities`}
                        unitLabel="active user"
                        autoFit={false}
                    />
                </div>
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-12 gap-4 mt-4">
                {statCards.map((c) => (
                    <div className="col-span-12 sm:col-span-6 lg:col-span-4" key={c.title}>
                        <div className="card h-full">
                            <Card title={c.title}>
                                <span title={c.exact} style={{ fontSize: "22px", fontWeight: 700, color: "#264164", cursor: "default" }}>{c.value}</span>
                            </Card>
                        </div>
                    </div>
                ))}
            </div>

            {/* Trend chart */}
            <div className="grid grid-cols-12 gap-4 mt-4">
                <div className="col-span-12">
                    <div className="card h-full">
                        <Card title="Users & Sessions">
                            <div style={{ height: "300px" }}>
                                <canvas ref={trendCanvasRef} />
                            </div>
                        </Card>
                    </div>
                </div>
            </div>

            {/* Acquisition — first-touch source, this-session source, and
                new vs returning users over time */}
            <div className="grid grid-cols-12 gap-4 mt-4">
                <div className="col-span-12 lg:col-span-4">
                    <div className="card h-full">
                        <Card title="Active Users by First User Source">
                            {firstUserSource.length === 0 ? (
                                <p className="text-sm m-0" style={{ color: "#9ca3af" }}>No data yet</p>
                            ) : (
                                <div className="flex flex-col">
                                    {firstUserSource.map((s) => (
                                        <div
                                            key={s.source}
                                            className="flex justify-between text-xs"
                                            style={{ color: "#374151", padding: "8px 0", borderBottom: "1px solid #e5e7eb" }}
                                        >
                                            <span>{s.source}</span>
                                            <span title={s.activeUsers.toLocaleString()} style={{ fontWeight: 600, cursor: "default" }}>{formatCompact(s.activeUsers)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>
                    </div>
                </div>
                <div className="col-span-12 lg:col-span-4">
                    <div className="card h-full">
                        <Card title="Sessions by Session Source">
                            {sessionSource.length === 0 ? (
                                <p className="text-sm m-0" style={{ color: "#9ca3af" }}>No data yet</p>
                            ) : (
                                <div className="flex flex-col">
                                    {sessionSource.map((s) => (
                                        <div
                                            key={s.source}
                                            className="flex justify-between text-xs"
                                            style={{ color: "#374151", padding: "8px 0", borderBottom: "1px solid #e5e7eb" }}
                                        >
                                            <span>{s.source}</span>
                                            <span title={s.sessions.toLocaleString()} style={{ fontWeight: 600, cursor: "default" }}>{formatCompact(s.sessions)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>
                    </div>
                </div>
                <div className="col-span-12 lg:col-span-4">
                    <div className="card h-full">
                        <Card title="New vs. Returning Users">
                            <div style={{ height: "220px" }}>
                                <canvas ref={nvrCanvasRef} />
                            </div>
                        </Card>
                    </div>
                </div>
            </div>

            {/* Traffic sources + Device breakdown + Top 5 Cities (quick glance —
                the full, all-cities report table is further down) */}
            <div className="grid grid-cols-12 gap-4 mt-4">
                <div className="col-span-12 lg:col-span-4">
                    <div className="card h-full">
                        <Card title="Traffic Sources">
                            {trafficSources.length === 0 ? (
                                <p className="text-sm m-0" style={{ color: "#9ca3af" }}>No data yet</p>
                            ) : (
                                <div className="flex flex-col gap-2">
                                    {trafficSources.map((s) => (
                                        <div key={s.channel}>
                                            <div className="flex justify-between text-xs mb-1" style={{ color: "#374151" }}>
                                                <span className="font-semibold">{s.channel}</span>
                                                <span title={s.sessions.toLocaleString()} style={{ cursor: "default" }}>{formatCompact(s.sessions)} sessions</span>
                                            </div>
                                            <div style={{ background: "#f3f4f6", borderRadius: "4px", height: "8px", overflow: "hidden" }}>
                                                <div style={{
                                                    width: `${(s.sessions / maxChannelSessions) * 100}%`,
                                                    background: CHANNEL_COLORS[s.channel] || "#2a8f9e",
                                                    height: "100%",
                                                }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>
                    </div>
                </div>
                <div className="col-span-12 lg:col-span-4">
                    <div className="card h-full">
                        <Card title="Device Breakdown">
                            {deviceBreakdown.length === 0 ? (
                                <p className="text-sm m-0" style={{ color: "#9ca3af" }}>No data yet</p>
                            ) : (
                                <div className="flex flex-col gap-2">
                                    {deviceBreakdown.map((d) => (
                                        <div key={d.device}>
                                            <div className="flex justify-between text-xs mb-1" style={{ color: "#374151" }}>
                                                <span className="font-semibold capitalize">{d.device}</span>
                                                <span title={d.activeUsers.toLocaleString()} style={{ cursor: "default" }}>{formatCompact(d.activeUsers)} users</span>
                                            </div>
                                            <div style={{ background: "#f3f4f6", borderRadius: "4px", height: "8px", overflow: "hidden" }}>
                                                <div style={{
                                                    width: `${(d.activeUsers / maxDeviceUsers) * 100}%`,
                                                    background: "#264164",
                                                    height: "100%",
                                                }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>
                    </div>
                </div>
                <div className="col-span-12 lg:col-span-4">
                    <div className="card h-full">
                        <Card title="Top Cities">
                            {cityBreakdown.length === 0 ? (
                                <p className="text-sm m-0" style={{ color: "#9ca3af" }}>No data yet</p>
                            ) : (
                                <div className="flex flex-col gap-2">
                                    {cityBreakdown.slice(0, 5).map((c) => (
                                        <div key={c.city}>
                                            <div className="flex justify-between text-xs mb-1" style={{ color: "#374151" }}>
                                                <span className="font-semibold">{c.city}</span>
                                                <span title={c.activeUsers.toLocaleString()} style={{ cursor: "default" }}>{formatCompact(c.activeUsers)} users</span>
                                            </div>
                                            <div style={{ background: "#f3f4f6", borderRadius: "4px", height: "8px", overflow: "hidden" }}>
                                                <div style={{
                                                    width: `${(c.activeUsers / Math.max(cityBreakdown[0]?.activeUsers || 1, 1)) * 100}%`,
                                                    background: "#f59e0b",
                                                    height: "100%",
                                                }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>
                    </div>
                </div>
            </div>

            {/* Full reports — like GA4's own "Demographic details" / "Pages
                and screens" tables: every row (not just a top-few summary),
                sortable, paginated. */}
            <div className="grid grid-cols-12 gap-4 mt-4">
                <div className="col-span-12">
                    <div className="overflow-x-auto rounded-md shadow-sm">
                        <DataTable
                            header={<h3 className="m-0">{source === "app" ? "Top Screens" : "Top Pages"} — Full Report</h3>}
                            value={topPages}
                            emptyMessage={source === "app" ? "No screen view data yet" : "No page view data yet"}
                            scrollable
                            resizableColumns
                            paginator
                            rows={10}
                            rowsPerPageOptions={[10, 25, 50]}
                            style={{ minWidth: "800px" }}
                        >
                            <Column field="title" header={source === "app" ? "Screen Name" : "Page Title"} sortable />
                            {source !== "app" && <Column field="path" header="Path" sortable />}
                            <Column field="views" header="Views" sortable body={(row: TopPage) => <CompactCell n={row.views} />} />
                            <Column field="activeUsers" header="Active Users" sortable body={(row: TopPage) => <CompactCell n={row.activeUsers} />} />
                            <Column field="engagedSessions" header="Engaged Sessions" sortable body={(row: TopPage) => <CompactCell n={row.engagedSessions} />} />
                            <Column field="engagementRate" header="Engagement Rate" sortable body={(row: TopPage) => formatPercent(row.engagementRate)} />
                            <Column
                                header={source === "app" ? "Avg Time on Screen" : "Avg Time on Page"}
                                sortable
                                field="avgDurationSec"
                                body={(row: TopPage) => formatDuration(row.avgDurationSec)}
                            />
                            <Column field="eventCount" header="Event Count" sortable body={(row: TopPage) => <CompactCell n={row.eventCount} />} />
                        </DataTable>
                    </div>
                </div>
            </div>


            <div className="grid grid-cols-12 gap-4 mt-4">
                <div className="col-span-12">
                    <div className="overflow-x-auto rounded-md shadow-sm">
                        <DataTable
                            header={<h3 className="m-0">Cities — Full Report</h3>}
                            value={cityBreakdown}
                            emptyMessage="No city data yet"
                            scrollable
                            resizableColumns
                            paginator
                            rows={10}
                            rowsPerPageOptions={[10, 25, 50]}
                            style={{ minWidth: "800px" }}
                        >
                            <Column field="city" header="City" sortable />
                            <Column field="activeUsers" header="Active Users" sortable body={(row: CityReportRow) => <CompactCell n={row.activeUsers} />} />
                            <Column field="newUsers" header="New Users" sortable body={(row: CityReportRow) => <CompactCell n={row.newUsers} />} />
                            <Column field="engagedSessions" header="Engaged Sessions" sortable body={(row: CityReportRow) => <CompactCell n={row.engagedSessions} />} />
                            <Column field="engagementRate" header="Engagement Rate" sortable body={(row: CityReportRow) => formatPercent(row.engagementRate)} />
                            <Column field="avgDurationSec" header="Avg Engagement Time" sortable body={(row: CityReportRow) => formatDuration(row.avgDurationSec)} />
                            <Column field="eventCount" header="Event Count" sortable body={(row: CityReportRow) => <CompactCell n={row.eventCount} />} />
                        </DataTable>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WebsiteAnalyticsPage;
